'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getFactionBossLeaderboard } from '@wwe-portal/web/services/client/leaderboard';
import {
  FactionLeaderboard,
  FactionLeaderboardSkeleton,
} from './faction-leaderboard';
import { LeaderboardHead } from './leaderboard-head';
import { MAX_FACTION_LEADERBOARD_LIMIT } from '@wwe-portal/web/utils/constants/leaderboard';
import { FC, useState } from 'react';
import { FactionLeaderboardRank } from '@wwe-portal/entities/leaderboard.type';
import { PaginationBar } from '../../../../../packages/ui/components/pagination/pagination-bar';
import { QUERY_KEYS_LEADERBOARD_FACTION_BOSS } from '@wwe-portal/web/utils/constants/query-key';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { LeaderboardLoadMoreButton } from './leaderboard-loadmore-button';

const PER_PAGE = 100;

const rankToFactionRankData = (
  faction: FactionLeaderboardRank,
  index: number
) => ({
  points: faction.factionPoints,
  rank: index + 1,
  factionId: faction.factionId,
  faction: faction.factionName,
  name: faction.factionName,
});

const Header: FC<{
  isLive: boolean;
  name: string;
}> = ({ isLive, name }) => (
  <LeaderboardHead isLive={isLive} name={name} moreHref="" moreText="" />
);

const InfiniteFactionFeudTable: React.FC<{
  eventId: string;
  type: 'factions' | 'faction-boss';
}> = ({ eventId, type }) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        QUERY_KEYS_LEADERBOARD_FACTION_BOSS,
        {
          eventId,
          type,
        },
      ],
      queryFn: async ({ pageParam }) => {
        const response = await getFactionBossLeaderboard({
          eventId,
          skip: pageParam * PER_PAGE,
          limit: PER_PAGE,
          type,
        });
        return response.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.data.leaderboard.length < PER_PAGE) {
          return undefined;
        }
        if (pages.length * PER_PAGE >= MAX_FACTION_LEADERBOARD_LIMIT) {
          return undefined;
        }
        return pages.length;
      },
    });

  const allData = data?.pages.flatMap((page) => page.data.leaderboard);
  const isLive = data?.pages[0]?.data.event.isLive ?? false;
  const header = <Header isLive={isLive} name={eventId} />;

  return (
    <div className="py-10">
      {!allData && (
        <FactionLeaderboardSkeleton
          nRows={PER_PAGE}
          desktopHeader={header}
          mobileHeader={header}
        />
      )}
      {allData && (
        <FactionLeaderboard
          data={allData.map(rankToFactionRankData)}
          mobileHeader={header}
          desktopHeader={header}
        />
      )}
      {hasNextPage && (
        <LeaderboardLoadMoreButton
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />
      )}
    </div>
  );
};

const PaginatedFactionFeudTable: FC<{
  perPage: number;
  eventId: string;
  type: 'factions' | 'faction-boss';
}> = ({ perPage, eventId, type }) => {
  const [page, setPage] = useState(0);
  const { data } = useQuery({
    queryKey: [
      QUERY_KEYS_LEADERBOARD_FACTION_BOSS,
      {
        eventId,
        page,
        perPage,
        type,
      },
    ],
    queryFn: async () => {
      const response = await getFactionBossLeaderboard({
        eventId,
        skip: page * perPage,
        limit: perPage,
        type,
      });
      return response.data;
    },
  });
  const isLive = data?.data.event.isLive ?? false;
  return (
    <div className="py-10">
      {!data?.data.leaderboard && (
        <FactionLeaderboardSkeleton
          nRows={PER_PAGE}
          mobileHeader={<Header isLive={isLive} name={eventId} />}
          desktopHeader={<Header isLive={isLive} name={eventId} />}
        />
      )}
      {data?.data.leaderboard && (
        <FactionLeaderboard
          data={data.data.leaderboard.map((item, index) =>
            rankToFactionRankData(item, index + page * perPage)
          )}
          mobileHeader={<Header isLive={isLive} name={eventId} />}
          desktopHeader={<Header isLive={isLive} name={eventId} />}
        />
      )}

      {data?.data.totalCount && (
        <PaginationBar
          page={page}
          totalPages={Math.ceil(Number(data.data.totalCount) / perPage)}
          showPages={5}
          setPage={setPage}
        />
      )}
    </div>
  );
};

export const FactionFeudTable: FC<{
  eventId: string;
  type: 'factions' | 'faction-boss';
}> = ({ eventId, type }) => {
  const { isHydrated, isMobile } = useResponsive();
  if (!isHydrated) {
    return null;
  }
  if (isMobile) {
    return <InfiniteFactionFeudTable eventId={eventId} type={type} />;
  }
  return (
    <PaginatedFactionFeudTable
      perPage={PER_PAGE}
      eventId={eventId}
      type={type}
    />
  );
};
